import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FileText, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from './AdminLayout';
import KeysTab from '../../components/developer-api/KeysTab';
import RequestLogTab from '../../components/developer-api/RequestLogTab';
import WebhooksTab from '../../components/developer-api/WebhooksTab';
import DocsTab from '../../components/developer-api/DocsTab';
import CreateKeyDialog from '../../components/developer-api/CreateKeyDialog';
import RevealKeyDialog from '../../components/developer-api/RevealKeyDialog';
import { RevokeKeyDialog, RollKeyDialog } from '../../components/developer-api/KeyActionDialogs';
import { errorText } from '../../lib/apiErrors';
import { getStats, listKeys, listTeam, listWebhooks } from '../../lib/developerApi';

const TABS = [
    { id: 'keys', label: 'Keys' },
    { id: 'hooks', label: 'Webhooks' },
    { id: 'log', label: 'Request log' },
    { id: 'docs', label: 'Docs' },
];

/**
 * Admin → Integrations → Developer API (#793 S5).
 *
 * Keys (mint, roll, revoke), webhook endpoints (S7), the request log and the
 * integration docs for the org's public API.
 */
export default function AdminDeveloperApiPage() {
    const { getAuthHeader, user } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = getAuthHeader()?.Authorization;
    // Memoised on the token string: getAuthHeader() returns a new object on every
    // call, and handing that to effects as a dependency would refetch forever.
    const headers = useMemo(() => (token ? { Authorization: token } : {}), [token]);

    const [tab, setTab] = useState(() => (TABS.some((t) => t.id === searchParams.get('tab')) ? searchParams.get('tab') : 'keys'));
    const [logKey, setLogKey] = useState(searchParams.get('key') || 'all');
    const [keys, setKeys] = useState([]);
    const [stats, setStats] = useState(null);
    const [team, setTeam] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [reveal, setReveal] = useState(null); // {result, graceLabel}
    const [rolling, setRolling] = useState(null);
    const [revoking, setRevoking] = useState(null);
    const [hookCount, setHookCount] = useState(null);

    const reload = useCallback(async () => {
        try {
            const [k, s] = await Promise.all([listKeys(headers), getStats(headers)]);
            setKeys(k);
            setStats(s);
        } catch (err) {
            toast.error(errorText(err, 'Could not load API keys.'));
        } finally {
            setLoading(false);
        }
    }, [headers]);

    useEffect(() => {
        reload();
    }, [reload]);

    useEffect(() => {
        listTeam(headers).then(setTeam).catch(() => setTeam([]));
        // The Webhooks tab shows its count before it is opened, like Keys.
        listWebhooks(headers).then((h) => setHookCount(h.endpoints.length)).catch(() => {});
    }, [headers]);

    const activeCount = keys.filter((k) => k.status === 'active').length;

    const openKey = (k) => navigate(`/admin/developer-api/keys/${k.key_id}`);

    const actions = (
        <>
            <button type="button" onClick={() => setTab('docs')} className="h-9 px-3.5 rounded-md border border-stone-200 bg-white text-sm font-medium inline-flex items-center gap-2 hover:bg-stone-50">
                <FileText className="w-4 h-4" /> API docs
            </button>
            <button
                type="button"
                data-testid="create-key-open"
                onClick={() => setCreateOpen(true)}
                className="h-9 px-3.5 rounded-md bg-stone-900 text-white text-sm font-medium inline-flex items-center gap-2 hover:bg-stone-800"
            >
                <Plus className="w-4 h-4" /> Create key
            </button>
        </>
    );

    return (
        <AdminLayout
            title="Developer API"
            description="Let other tools create and update contacts and companies, using keys you control."
            actions={actions}
        >
            <div role="tablist" className="flex gap-6 border-b border-stone-200 -mt-2 mb-5">
                {TABS.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        role="tab"
                        aria-selected={tab === t.id}
                        data-testid={`tab-${t.id}`}
                        onClick={() => setTab(t.id)}
                        className={`pt-2 pb-3 -mb-px text-sm font-medium border-b-2 inline-flex items-center gap-1.5 ${tab === t.id ? 'text-stone-900 border-stone-700' : 'text-stone-500 border-transparent hover:text-stone-700'}`}
                    >
                        {t.label}
                        {t.id === 'keys' && <span className="text-[11px] bg-stone-100 text-stone-600 rounded-full px-1.5">{activeCount}</span>}
                        {t.id === 'hooks' && hookCount != null && <span className="text-[11px] bg-stone-100 text-stone-600 rounded-full px-1.5">{hookCount}</span>}
                    </button>
                ))}
            </div>

            {loading && tab === 'keys' ? (
                <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-stone-400 animate-spin" /></div>
            ) : tab === 'keys' ? (
                <KeysTab
                    keys={keys}
                    stats={stats}
                    onOpen={openKey}
                    onEditPermissions={(k) => navigate(`/admin/developer-api/keys/${k.key_id}?edit=permissions`)}
                    onRoll={(k) => k && setRolling(k)}
                    onRevoke={setRevoking}
                />
            ) : tab === 'hooks' ? (
                <WebhooksTab headers={headers} onCount={setHookCount} />
            ) : tab === 'log' ? (
                <RequestLogTab keys={keys} headers={headers} initialKeyId={logKey} />
            ) : (
                <DocsTab />
            )}

            <CreateKeyDialog
                open={createOpen}
                onOpenChange={setCreateOpen}
                team={team}
                defaultOwnerId={user?.user_id}
                headers={headers}
                onCreated={(created) => {
                    setCreateOpen(false);
                    setReveal({ result: created, graceLabel: null });
                    reload();
                }}
            />
            <RollKeyDialog
                apiKey={rolling}
                headers={headers}
                onClose={() => setRolling(null)}
                onRolled={(rolled, graceLabel) => {
                    setRolling(null);
                    setReveal({ result: rolled, graceLabel });
                    reload();
                }}
            />
            <RevokeKeyDialog
                apiKey={revoking}
                headers={headers}
                onClose={() => setRevoking(null)}
                onRollInstead={() => {
                    setRolling(revoking);
                    setRevoking(null);
                }}
                onRevoked={() => {
                    setRevoking(null);
                    reload();
                }}
            />
            <RevealKeyDialog
                result={reveal?.result}
                rolledGraceLabel={reveal?.graceLabel}
                onClose={() => {
                    setReveal(null);
                    setLogKey('all');
                }}
            />
        </AdminLayout>
    );
}
